import { provideHttpClient, withInterceptors } from '@angular/common/http';
import type { ApplicationConfig } from '@angular/core';
import { APP_INITIALIZER, importProvidersFrom } from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatCalendarHeader } from '@angular/material/datepicker';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import type { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalModule,
  MsalService,
} from '@azure/msal-angular';
import { BrowserCacheLocation, InteractionType, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { provideSpri } from '@spri';
import { CustomCalendarHeader } from '@spri/components/single-date-picker/custom-calendar-header';
import { spriLoadingInterceptor } from '@spri/services/loading';
import { appRoutes } from 'app/app.routes';
import { provideIcons } from 'app/core/icons/icons.provider';
import { provideMetrics } from 'app/core/metrics';
import { TenantBrandingCssService, TenantBrandingService, TenantBrandingStore } from 'app/core/tenant-branding';
import { MockApiService } from 'app/mock-api';
import { environment } from 'environments/environment';
import { firstValueFrom } from 'rxjs';
// Authentication and HTTP interceptors for token management and error handling
import { authErrorInterceptor } from './core/auth/auth-error.interceptor';
import { authInterceptor } from './core/auth/auth.interceptor';
import { provideAuth } from './core/auth/auth.provider';
import { tokenRefreshInterceptor } from './core/auth/token-refresh.interceptor';
// import { AuthAuditService } from './core/auth/audit.service';

function msalInstanceFactory(): PublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msal.clientId,
      authority: environment.msal.authority,
      redirectUri: environment.msal.redirectUri,
      postLogoutRedirectUri: environment.msal.postLogoutRedirectUri,
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage,
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        logLevel: LogLevel.Warning,
      },
    },
  });
}

function msalGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: { scopes: [...environment.basicScopes, ...environment.api.scopes] },
  };
}

function msalInterceptorConfigFactory(): MsalInterceptorConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap: new Map([[environment.api.baseUrl, [...environment.api.scopes]]]),
  };
}

// MSAL initialization function
function initializeMsal(msalInstance: PublicClientApplication): () => Promise<void> {
  return (): Promise<void> => {
    return msalInstance.initialize();
  };
}

// Build interceptor array with proper execution order
// IMPORTANT: Interceptor execution order is left-to-right (left executes first)
const interceptors = [
  // 1. authInterceptor - adds Bearer token to requests
  authInterceptor,
  // 2. tokenRefreshInterceptor - handles 401 errors by refreshing expired tokens
  //    Must come after authInterceptor so tokens are added before refresh attempts
  tokenRefreshInterceptor,
  // 3. authErrorInterceptor - standardizes error responses and logs authentication issues
  //    Must come after tokenRefreshInterceptor so refresh errors are properly formatted
  authErrorInterceptor,
  // 4. spriLoadingInterceptor - tracks loading state for the application
  //    Comes last to track all other interceptor activity
  spriLoadingInterceptor,
];

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    // Register all HTTP interceptors
    // Execution order matters: interceptors execute left-to-right
    // Request phase:  authInterceptor → tokenRefreshInterceptor → authErrorInterceptor → spriLoadingInterceptor
    // Response phase: spriLoadingInterceptor → authErrorInterceptor → tokenRefreshInterceptor → authInterceptor
    provideHttpClient(withInterceptors(interceptors)),
    { provide: MSAL_GUARD_CONFIG, useFactory: msalGuardConfigFactory },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: msalInterceptorConfigFactory },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    MsalService,
    MsalBroadcastService,
    MsalGuard,
    importProvidersFrom(
      MsalModule.forRoot(
        msalInstanceFactory(), // Pass the same instance from factory, or better, reuse the provided MSAL_INSTANCE
        msalGuardConfigFactory(),
        msalInterceptorConfigFactory()
      )
    ),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMsal,
      deps: [MSAL_INSTANCE], // Use the MSAL_INSTANCE provided above
      multi: true,
    },

    // Load tenant branding before bootstrap and apply CSS variables
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [TenantBrandingStore],
      useFactory: (store: TenantBrandingStore) => {
        return () => firstValueFrom(store.loadTenantBranding());
      },
    },

    // SPA OTEL metrics: page-load capture (Phase 2D).
    // Initializes WebTracerProvider, registers MetricsSpanProcessor,
    // starts router + app-bootstrap trackers, and hooks unload-flush.
    provideMetrics(),

    provideRouter(appRoutes, withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),

    // Material Date Adapter
    { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
    {
      provide: DateAdapter,
      useClass: LuxonDateAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'D',
        },
        display: {
          dateInput: 'DDD',
          monthYearLabel: 'LLL yyyy',
          dateA11yLabel: 'DD',
          monthYearA11yLabel: 'LLLL yyyy',
        },
      },
    },
    // Global custom header for Material datepicker
    { provide: MatCalendarHeader, useValue: CustomCalendarHeader },

    // Tenant Branding Services
    TenantBrandingService,
    TenantBrandingCssService,
    TenantBrandingStore,

    // Auth
    ...provideAuth(),

    // Spri
    provideIcons(),
    provideSpri({
      mockApi: {
        delay: 0,
        service: MockApiService,
      },
      spri: {
        layout: 'spri',
        scheme: 'light',
        screens: {
          sm: '600px',
          md: '960px',
          lg: '1280px',
          xl: '1440px',
        },
        theme: 'theme-default',
        themes: [
          {
            id: 'theme-default',
            name: 'Default',
          },
          {
            id: 'theme-brand',
            name: 'Brand',
          },
          {
            id: 'theme-teal',
            name: 'Teal',
          },
          {
            id: 'theme-rose',
            name: 'Rose',
          },
          {
            id: 'theme-purple',
            name: 'Purple',
          },
          {
            id: 'theme-amber',
            name: 'Amber',
          },
        ],
      },
    }),

    // Initialize auth audit logging - disabled until backend endpoint is implemented
    // provideAppInitializer(() => {
    //     const audit = inject(AuthAuditService);
    //     audit.init();
    // }),
  ],
};
